import { useEffect, useRef, type RefObject } from "react";
import { useNavigate } from "react-router-dom";

// Swipe in from the left edge to go back a page.
//
// On the phone this app is a web view, and a web view gets none of iOS's own
// edge-swipe: the only way back out of a recipe was the browser chrome the app
// doesn't have, or a tap on a nav item. So the gesture is rebuilt here.
//
// What it does *not* do is drag the current page off to the right, which is
// the obvious way to build this and the wrong one: putting a transform on the
// shell makes it the containing block for every `position: fixed` descendant,
// so the bottom tab bar would leap to the end of the document the moment a
// finger touched the edge. Instead the *arriving* page slides in over the
// current one — a plain sheet in the page's own background colour, which is
// what the eye expects from a back gesture anyway (the previous screen comes
// from the left) and which is honest about the fact that we cannot paint the
// previous screen's content until the router has actually gone there.

/** How far in from the left edge a swipe has to start, in viewport px. */
const EDGE = 28;
/** How far it has to travel before we call it a swipe and not a scroll. */
const SLOP = 10;
/** Past this fraction of the screen, letting go goes back. */
const COMMIT = 0.32;
/** …or a flick at this speed (px/ms) that has still covered some ground. */
const FLICK = 0.4;
const FLICK_MIN = 60;
/** Settle animations: landing the sheet, and springing it back. */
const LAND_MS = 130;
const CANCEL_MS = 180;
const FADE_MS = 130;
/** Long enough for the page we landed on to have painted. */
const PAINT_MS = 32;

type Drag = {
  id: number;
  x0: number;
  y0: number;
  x: number;
  t: number;
  /** True once the gesture is unambiguously a horizontal edge pull. */
  live: boolean;
};

/**
 * Whether there is an app page behind this one.
 *
 * React Router stamps each entry it pushes with its index in the session's
 * stack, so anything above zero has somewhere of ours to go back to. A cold
 * open, or a shared link opened straight into a recipe, sits at zero — and
 * going back from there would leave the app entirely, which is not what a
 * swipe should ever do.
 */
function canGoBack(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === "number" && idx > 0;
}

function reducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * True if the touch landed in something that scrolls sideways and is scrolled
 * off its left end — a chip rail, a wide table. Dragging right there means
 * "scroll back", not "go back".
 */
function insideScrolledRow(from: Element, stopAt: Element): boolean {
  let el: Element | null = from;
  while (el && el !== stopAt) {
    if (el.scrollLeft > 0) return true;
    el = el.parentElement;
  }
  return false;
}

export function useSwipeBack(shellRef: RefObject<HTMLElement>) {
  const navigate = useNavigate();

  // Held at arm's length rather than named as a dependency below. A gesture
  // runs for the best part of a second, and useNavigate hands back a new
  // function whenever the router re-renders — depend on it directly and any
  // re-render mid-swipe tears the listeners down and takes the half-finished
  // gesture with it, which is exactly what happened: the finger lifted, the
  // page never went back.
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    let drag: Drag | null = null;
    let sheet: HTMLDivElement | null = null;
    let timer: number | undefined;

    // Body, not the shell: #root carries the reading-size zoom, so a length
    // set inside it is multiplied on the way to the screen and the sheet would
    // outrun the finger at anything but 100%. Out here, one px is one px —
    // the same reasoning as the tour spotlight's portal.
    function open(): HTMLDivElement {
      const el = document.createElement("div");
      el.className = "swipe-back";
      el.setAttribute("aria-hidden", "true");
      const arrow = document.createElement("div");
      arrow.className = "swipe-back-arrow";
      arrow.textContent = "‹";
      el.append(arrow);
      document.body.append(el);
      return el;
    }

    function close() {
      window.clearTimeout(timer);
      timer = undefined;
      sheet?.remove();
      sheet = null;
    }

    /** Let go of the finger: the gesture is over, however it ended. */
    function detach() {
      drag = null;
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    }

    /**
     * Drop everything where it stands, animating nothing.
     *
     * Used when the app goes away mid-swipe — a lock screen, an app switch.
     * Whatever it was going to do, it doesn't finish it behind your back and
     * doesn't leave the sheet lying over the app to be found on your return.
     */
    function abandon() {
      detach();
      close();
    }

    function paint(dx: number) {
      if (!sheet) return;
      const w = Math.max(0, Math.min(dx, window.innerWidth));
      sheet.style.width = `${w}px`;
      // The chevron arrives over the first stretch of the pull, so the gesture
      // says what it is before you have committed to it.
      const progress = Math.min(1, w / (window.innerWidth * COMMIT));
      sheet.style.setProperty("--swipe-progress", String(progress));
    }

    function settle(el: HTMLDivElement, width: string, ms: number, done: () => void) {
      const dur = reducedMotion() ? 0 : ms;
      el.style.transition = `width ${dur}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
      el.style.width = width;
      timer = window.setTimeout(done, dur);
    }

    function release(commit: boolean) {
      const el = sheet;
      detach();
      if (!el) return;

      if (!commit) {
        settle(el, "0px", CANCEL_MS, close);
        return;
      }

      settle(el, `${window.innerWidth}px`, LAND_MS, () => {
        navRef.current(-1);
        // The sheet is already the colour of the page landing behind it, so
        // holding it for a beat and then dissolving reads as the page settling
        // rather than as a cut. A timer rather than the frame callback this
        // asks for: a phone that locks mid-swipe stops painting frames
        // entirely, and a teardown that waits for one that never comes leaves
        // the sheet stranded over the app.
        timer = window.setTimeout(() => {
          el.style.transition = reducedMotion() ? "none" : `opacity ${FADE_MS}ms linear`;
          el.style.opacity = "0";
          timer = window.setTimeout(close, reducedMotion() ? 0 : FADE_MS);
        }, PAINT_MS);
      });
    }

    function onStart(e: TouchEvent) {
      if (drag || e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE) return;
      if (!canGoBack()) return;

      const shell = shellRef.current;
      const target = e.target;
      if (!shell || !(target instanceof Element) || !shell.contains(target)) return;
      // Everything that floats above the page — the chat, the tour, the
      // opening titles — is mounted outside the shell, so the check above has
      // already excluded it. These two live inside it: cooking mode, which
      // spends the same gesture on the previous step, and any open modal,
      // which you close rather than swipe past.
      if (target.closest('[data-swipe-back="off"], .modal-backdrop')) return;
      if (insideScrolledRow(target, shell)) return;

      drag = { id: t.identifier, x0: t.clientX, y0: t.clientY, x: t.clientX, t: e.timeStamp, live: false };
      // A non-passive touchmove listener costs every scroll on the page, so it
      // only exists while a gesture might still become ours.
      window.addEventListener("touchmove", onMove, { passive: false });
      window.addEventListener("touchend", onEnd);
      window.addEventListener("touchcancel", onEnd);
    }

    function onMove(e: TouchEvent) {
      if (!drag) return;
      const t = Array.from(e.touches).find((c) => c.identifier === drag!.id);
      if (!t) return;
      const dx = t.clientX - drag.x0;
      const dy = t.clientY - drag.y0;

      if (!drag.live) {
        // Whichever axis clears the slop first wins the gesture outright, so a
        // thumb sliding up the edge of a long list still scrolls it.
        if (Math.abs(dy) > SLOP && Math.abs(dy) >= Math.abs(dx)) {
          release(false);
          return;
        }
        if (dx < SLOP) return;
        drag.live = true;
        close();
        sheet = open();
      }

      // Ours now: keep the page underneath from scrolling out from under it.
      e.preventDefault();
      drag.x = t.clientX;
      drag.t = e.timeStamp;
      paint(dx);
    }

    function onEnd(e: TouchEvent) {
      if (!drag) return;
      if (!drag.live) {
        release(false);
        return;
      }
      const t = Array.from(e.changedTouches).find((c) => c.identifier === drag!.id);
      const x = t ? t.clientX : drag.x;
      const dx = x - drag.x0;
      const dt = e.timeStamp - drag.t;
      const v = dt > 0 ? (x - drag.x) / dt : 0;
      release(dx > window.innerWidth * COMMIT || (dx > FLICK_MIN && v > FLICK));
    }

    function onHide() {
      if (document.hidden) abandon();
    }

    window.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("touchstart", onStart);
      document.removeEventListener("visibilitychange", onHide);
      abandon();
    };
  }, [shellRef]);
}
